import { useState, useRef, useEffect, useCallback } from "react";
import { cn } from "@/lib/utils";
import { ChevronLeft, ChevronRight } from "lucide-react";

interface GalleryImage {
  id: string;
  image_url: string;
  caption?: string | null;
  sort_order: number;
}

interface ProductGalleryProps {
  images: GalleryImage[];
  title: string;
}

export function ProductGallery({ images, title }: ProductGalleryProps) {
  const allImages = images
    .sort((a, b) => a.sort_order - b.sort_order)
    .map((img) => ({ url: img.image_url, caption: img.caption }));

  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const thumbnailContainerRef = useRef<HTMLDivElement>(null);
  const swipeStartRef = useRef<{ x: number; y: number; pointerId: number } | null>(null);

  const checkScrollability = useCallback(() => {
    const container = thumbnailContainerRef.current;
    if (!container) return;
    setCanScrollLeft(container.scrollLeft > 4);
    setCanScrollRight(container.scrollLeft + container.clientWidth < container.scrollWidth - 4);
  }, []);

  useEffect(() => {
    checkScrollability();
    const container = thumbnailContainerRef.current;
    if (!container) return;
    container.addEventListener("scroll", checkScrollability);
    const observer = new ResizeObserver(checkScrollability);
    observer.observe(container);
    return () => {
      container.removeEventListener("scroll", checkScrollability);
      observer.disconnect();
    };
  }, [checkScrollability, allImages.length]);

  // Scroll selected thumbnail into view
  useEffect(() => {
    const container = thumbnailContainerRef.current;
    if (!container) return;
    const thumb = container.children[0]?.children[selectedIndex] as HTMLElement;
    if (thumb) {
      thumb.scrollIntoView({ behavior: "smooth", inline: "nearest", block: "nearest" });
    }
  }, [selectedIndex]);

  if (allImages.length === 0) return null;

  const scrollThumbnails = (direction: "left" | "right") => {
    const container = thumbnailContainerRef.current;
    if (!container) return;
    const scrollAmount = 200;
    container.scrollBy({
      left: direction === "left" ? -scrollAmount : scrollAmount,
      behavior: "smooth",
    });
  };

  const selectImage = (index: number) => {
    if (index === selectedIndex) return;
    setIsTransitioning(true);
    setTimeout(() => {
      setSelectedIndex(index);
      setTimeout(() => setIsTransitioning(false), 50);
    }, 200);
  };

  const handlePrev = () => {
    selectImage(selectedIndex === 0 ? allImages.length - 1 : selectedIndex - 1);
  };

  const handleNext = () => {
    selectImage(selectedIndex === allImages.length - 1 ? 0 : selectedIndex + 1);
  };

  const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (event.pointerType === "mouse") return;
    // Skip swipe tracking when the tap starts on a control (arrows, dots) so
    // pointer capture doesn't swallow their click events.
    if ((event.target as HTMLElement).closest("button")) return;
    swipeStartRef.current = { x: event.clientX, y: event.clientY, pointerId: event.pointerId };
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const handlePointerUp = (event: React.PointerEvent<HTMLDivElement>) => {
    const start = swipeStartRef.current;
    swipeStartRef.current = null;
    if (!start || start.pointerId !== event.pointerId || allImages.length < 2) return;

    const horizontalDistance = event.clientX - start.x;
    const verticalDistance = event.clientY - start.y;
    if (Math.abs(horizontalDistance) < 48 || Math.abs(horizontalDistance) <= Math.abs(verticalDistance)) return;

    if (horizontalDistance < 0) handleNext();
    else handlePrev();
  };

  const handlePointerCancel = () => {
    swipeStartRef.current = null;
  };

  return (
    <div className="flex flex-col gap-4">
      {/* Main image */}
      <div
        className="relative flex-1 overflow-hidden group bg-[hsl(0,0%,95%)] touch-pan-y select-none"
        onPointerDown={handlePointerDown}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerCancel}
      >
        <img
          src={allImages[selectedIndex].url}
          alt={
            allImages[selectedIndex].caption ||
            `${title} board game by Rogue Skull Games — component and artwork view ${selectedIndex + 1}`
          }
          className={cn(
            "w-full aspect-[4/3] sm:aspect-[3/2] xl:aspect-auto xl:h-[600px] object-cover pointer-events-none transition-opacity duration-300 ease-in-out",
            isTransitioning ? "opacity-0" : "opacity-100",
          )}
          draggable={false}
        />

        {allImages.length > 1 && (
          <>
            <button
              onClick={handlePrev}
              className="absolute left-3 top-1/2 -translate-y-1/2 w-[60px] h-[60px] bg-dark/80 hover:bg-dark border border-border flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300"
              aria-label="Previous image"
            >
              <ChevronLeft className="w-8 h-8 text-dark-foreground" />
            </button>
            <button
              onClick={handleNext}
              className="absolute right-3 top-1/2 -translate-y-1/2 w-[60px] h-[60px] bg-dark/80 hover:bg-dark border border-border flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300"
              aria-label="Next image"
            >
              <ChevronRight className="w-8 h-8 text-dark-foreground" />
            </button>
          </>
        )}

        {/* Mobile and tablet pagination dots */}
        {allImages.length > 1 && (
          <div className="absolute bottom-4 left-0 right-0 z-20 flex justify-center px-3 xl:hidden">
            <div className="gallery-indicators max-w-full">
              {allImages.map((_, index) => (
                <button
                  key={index}
                  onClick={() => selectImage(index)}
                  aria-label={`Show image ${index + 1} of ${allImages.length}`}
                  aria-current={selectedIndex === index ? "true" : undefined}
                  className={cn("gallery-indicator", selectedIndex === index && "active")}
                />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Thumbnail strip - desktop only, horizontal below main image */}
      {allImages.length > 1 && (
        <div className="hidden md:flex items-center relative group/thumbs h-[100px] min-w-0">
          {/* Scroll left arrow - overlays on hover */}
          <button
            className={cn(
              "absolute left-0 top-0 bottom-0 z-10 flex items-center justify-center px-2 transition-all duration-300 bg-gradient-to-r from-[hsl(0,0%,0%)] to-transparent opacity-0",
              canScrollLeft ? "group-hover/thumbs:opacity-100 cursor-pointer" : "pointer-events-none",
            )}
            onClick={() => scrollThumbnails("left")}
            aria-label="Scroll thumbnails left"
          >
            <ChevronLeft className="w-8 h-8 text-[hsl(0,0%,30%)]" />
          </button>

          {/* Thumbnails container */}
          <div
            ref={thumbnailContainerRef}
            className="thumb-strip flex flex-row gap-3 overflow-x-auto overscroll-x-contain w-full h-full px-1 touch-pan-x"
            style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
          >
            <style>{`.thumb-strip::-webkit-scrollbar { display: none; }`}</style>
            <div className="flex flex-row gap-3">
              {allImages.map((img, index) => (
                <button
                  key={index}
                  onClick={() => selectImage(index)}
                  className={cn(
                    "overflow-hidden w-[100px] h-[100px] overflow-hidden shrink-0 transition-all duration-300 border-2",
                    selectedIndex === index
                      ? "border-[hsl(0,0%,20%)] shadow-md"
                      : "border-transparent opacity-70 hover:opacity-100 hover:border-[hsl(0,0%,80%)]",
                  )}
                >
                  <img
                    src={img.url}
                    alt={img.caption || `${title} board game photo ${index + 1} thumbnail`}
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>
          </div>

          {/* Scroll right arrow - overlays on hover */}
          <button
            className={cn(
              "absolute right-0 top-0 bottom-0 z-10 flex items-center justify-center px-2 transition-all duration-300 bg-gradient-to-l from-[hsl(0,0%,0%)] to-transparent opacity-0",
              canScrollRight ? "group-hover/thumbs:opacity-100 cursor-pointer" : "pointer-events-none",
            )}
            onClick={() => scrollThumbnails("right")}
            aria-label="Scroll thumbnails right"
          >
            <ChevronRight className="w-8 h-8 text-[hsl(0,0%,30%)]" />
          </button>
        </div>
      )}
    </div>
  );
}
